import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
	Laugh,
	Angry,
	Smile,
	Meh,
	Frown,
	Save,
	CheckCircle,
	ArrowRight,
	ArrowLeft,
	PartyPopper,
	Bell,
	Lightbulb,
	Bookmark,
	ExternalLink,
} from 'lucide-react';
import Banner from '../../components/layout/Banner';
import PageTitle from '../../components/layout/PageTitle';
import { appFeatures } from '../../constants/features';
import { useMockUser } from '../../context/MockUserContext';
import {
	todayISO,
	nowTimeValue,
	formatTime,
	formatMediumDate,
} from '../../utils/journeyDates';
import {
	saveRatingLog,
	saveDiaryEntry,
	fetchCareTip,
	fetchRatingLogs,
	favoriteCareTip,
} from '../../services/trackingApi';
import styles from './TrackingPage.module.css';

const NO_NEW_TIPS_MESSAGE = 'No new tips today.';

// How long the freshly-loaded care tip stays highlighted after a submission.
const TIP_HIGHLIGHT_MS = 1800;

// How long the page stays put after the last goal of the day before the
// celebration takes it over. Enough to read the tip that submission just
// produced — at 1.2s it was gone before anyone had found it on the page.
// Exported so the tests wait on this number rather than a copy of it.
export const COMPLETION_DELAY_MS = 6000;

/** ['9:00 AM', '2:30 PM', '6:15 PM'] -> '9:00 AM, 2:30 PM and 6:15 PM' */
const joinTimes = (times) =>
	times.length <= 1
		? times.join('')
		: `${times.slice(0, -1).join(', ')} and ${times[times.length - 1]}`;

const TrackingPage = ({
	onLogoClick,
	currentGoal,
	allGoals = [],
	currentGoalIndex = 0,
	onNextGoal,
	onNavigateToJourney,
}) => {
	// State for tracking data
	const [selectedRating, setSelectedRating] = useState(null);
	const [comment, setComment] = useState('');
	const [isSaved, setIsSaved] = useState(false);
	const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
	// Bumped on every submission and used as the confirmation's key, so a second
	// submission remounts the line and replays its animation. Standing text that
	// never moves cannot tell you whether it is answering this save or the last.
	const [saveTick, setSaveTick] = useState(0);
	// Which tip this visit kept, rather than whether it kept one. A later save
	// reloads the card with a different tip, and a bare flag left the line
	// "Saved successfully!" standing beside a new, unsaved tip. Only the button
	// sets it: a tip that arrives already saved is shown locked but silent,
	// because the line reports an action, not a state.
	const [savedTipLogId, setSavedTipLogId] = useState(null);
	const [showCompletion, setShowCompletion] = useState(false);
	// Every rating this person has logged, so the page can say what is already
	// submitted for the day being filled in. This used to be a Set built up as
	// you saved, which knew nothing about earlier visits: rate three goals in
	// the morning and the fourth after lunch and the day never counted as done.
	const [ratingLogs, setRatingLogs] = useState([]);
	const [careTip, setCareTip] = useState(null);
	// Account-level nudge shown when there are no tips today (e.g. re-engagement)
	const [prompt, setPrompt] = useState(null);

	// When the entry is *for*. Defaults to right now, so tracking as you go
	// needs no thought; changing it lets someone catch up on a day they missed.
	const [entryDate, setEntryDate] = useState(todayISO);
	const [entryTime, setEntryTime] = useState(nowTimeValue);
	// Whether the person has moved either control off now, which is the only
	// reliable way to know: `entryTime` drifts from the clock on its own.
	const [whenEdited, setWhenEdited] = useState(false);
	// Draws attention to a tip that has just arrived, which otherwise appears
	// below the fold and goes unread.
	const [tipRevealed, setTipRevealed] = useState(false);

	// Ref for scrolling to top
	const contentRef = useRef(null);
	const careTipRef = useRef(null);
	const timers = useRef([]);
	// The pending celebration, held so it can be called off. Two quick saves
	// would otherwise schedule two, and moving to another goal inside the delay
	// would have the page replaced part-way through the next entry.
	const completionTimer = useRef(null);

	const { currentUserId } = useMockUser();

	// Reading these off the goal keeps them out of the hook dependency lists as
	// whole objects, which change identity on every render of the parent.
	const currentGoalId = currentGoal?.id;
	const currentGoalSettingId = currentGoal?.goal_setting_id;

	const track = (id) => {
		timers.current.push(id);
		return id;
	};

	useEffect(
		() => () => {
			timers.current.forEach(clearTimeout);
		},
		[],
	);

	// Returns the tip that ended up on screen, so the caller can tell whether
	// there is anything worth drawing attention to.
	const loadCareTip = useCallback(
		() =>
			fetchCareTip(currentUserId)
				.then((data) => {
					const tips = data?.tips ?? [];

					// Show the account-level prompt only when there are no tips today
					setPrompt(tips.length === 0 ? (data?.prompt ?? null) : null);

					const termId = Number(currentGoalId?.replace('term_', ''));
					const matchedTip = tips.find(
						(item) => item.care_tip.term_id === termId,
					);

					const next = matchedTip
						? {
								logId: matchedTip.id,
								message: matchedTip.care_tip.message,
								isFavorite: matchedTip.is_favorite,
								// Reading a tip is half of it; the link is where the
								// person goes to act on it. Absent on older tips.
								resources: matchedTip.care_tip.resources ?? [],
							}
						: null;
					setCareTip(next);
					return next;
				})
				.catch((err) => {
					console.error('Failed to fetch care tip:', err);
					setCareTip(null);
					setPrompt(null);
					return null;
				}),
		[currentUserId, currentGoalId],
	);

	const loadRatingLogs = useCallback(
		() =>
			fetchRatingLogs(currentUserId)
				.then((logs) => {
					const list = logs ?? [];
					setRatingLogs(list);
					return list;
				})
				.catch((err) => {
					console.error('Failed to load rating logs:', err);
					return [];
				}),
		[currentUserId],
	);

	// Keep the current care tip. There is no un-keep here — the button locks
	// once it lands, and a tip is dropped from the Resources page, where the
	// whole collection is in view and removing one is a considered act.
	const handleSaveTip = () => {
		if (!careTip || careTip.isFavorite) return;

		const { logId } = careTip;

		// Optimistically update, revert on failure
		setCareTip((prev) => prev && { ...prev, isFavorite: true });
		setSavedTipLogId(logId);

		favoriteCareTip(logId, currentUserId).catch((err) => {
			console.error('Failed to save care tip:', err);
			setCareTip((prev) => prev && { ...prev, isFavorite: false });
			// The confirmation goes with it: left standing it claims a save that
			// did not happen, and nothing else on screen would contradict it.
			setSavedTipLogId((current) => (current === logId ? null : current));
		});
	};

	useEffect(() => {
		if (!currentUserId || !currentGoalId) return;
		loadCareTip();
	}, [currentUserId, currentGoalId, loadCareTip]);

	useEffect(() => {
		if (!currentUserId) return;
		loadRatingLogs();
	}, [currentUserId, loadRatingLogs]);

	// Get the tracking feature data for consistent styling
	const trackingFeature = appFeatures.find(
		(feature) => feature.id === 'tracking',
	);

	// Rating scale configuration - removed labels, added lighter background colors
	//
	// The value stored is severity, not mood: the API takes 1 as mildest and 5 as
	// most severe, which is how the Journey page colours its faces and how the AI
	// text reads the number ("average reported severity ... out of 5"). The row
	// still reads best-to-worst left to right.
	const ratingScale = [
		{ value: 1, icon: Laugh, color: '#10B981', bgColor: '#ECFDF5' },
		{ value: 2, icon: Smile, color: '#84CC16', bgColor: '#F7FEE7' },
		{ value: 3, icon: Meh, color: '#F59E0B', bgColor: '#FFFBEB' },
		{ value: 4, icon: Frown, color: '#F97316', bgColor: '#FFF7ED' },
		{ value: 5, icon: Angry, color: '#EF4444', bgColor: '#FEF2F2' },
	];

	// Reset form and scroll to top when goal changes. The chosen date and time
	// deliberately survive: filling in a missed day means walking every goal at
	// that same moment, and re-picking it on each one is the whole chore.
	useEffect(() => {
		setSelectedRating(null);
		setComment('');
		setIsSaved(false);
		setShowSaveConfirmation(false);
		setSavedTipLogId(null);
		// Moving on is an answer to the celebration too: taking the page away
		// part-way through the next goal's entry is the thing this whole delay
		// exists to avoid. The Journey stays one tap away in the navigation.
		clearTimeout(completionTimer.current);

		// Scroll to top when goal changes
		if (contentRef.current) {
			contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, [currentGoal?.id]);

	// Anything that changes what would be submitted puts the button back to
	// "Save". Leaving it reading "Saved" after an edit claims the change is
	// already stored, and the change most likely to be missed is the date: the
	// whole point of moving it is to file a second entry on another day.
	//
	// The confirmation below it is deliberately left alone: it reports on the
	// entry that *was* filed, which editing the form does not undo.
	const markUnsaved = () => {
		setIsSaved(false);
	};

	const resetWhenToNow = () => {
		setEntryDate(todayISO());
		setEntryTime(nowTimeValue());
		setWhenEdited(false);
		markUnsaved();
	};

	// What is already on the server for the day being filled in. Without this
	// the page gives no sign that a goal has been rated, and someone part way
	// through a day cannot tell what is left.
	const logsOnEntryDate = ratingLogs.filter((log) => log.date === entryDate);

	const submittedTimes = logsOnEntryDate
		.filter(
			(log) =>
				currentGoalSettingId != null &&
				String(log.goal_setting_id) === String(currentGoalSettingId),
		)
		.map((log) => log.start_time)
		.sort()
		.map(formatTime);

	// True once every goal has at least one rating on that date, whether it was
	// logged in this visit or three hours ago.
	const coversEveryGoal = (logs, date) => {
		if (allGoals.length === 0) return false;
		const covered = new Set(
			logs
				.filter((log) => log.date === date)
				.map((log) => String(log.goal_setting_id)),
		);
		return allGoals.every(
			(goal) =>
				goal.goal_setting_id != null &&
				covered.has(String(goal.goal_setting_id)),
		);
	};

	const revealCareTip = () => {
		careTipRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		setTipRevealed(true);
		track(setTimeout(() => setTipRevealed(false), TIP_HIGHLIGHT_MS));
	};

	// Handle rating selection
	const handleRatingSelect = (rating) => {
		setSelectedRating(rating);
		markUnsaved();
	};

	// Handle save
	const handleSave = () => {
		if (!selectedRating) return;

		const goalSettingId = currentGoalSettingId;
		// The date is read once: the controls stay live while the request is in
		// flight, and the answer has to be judged against the day that was saved.
		const savedForDate = entryDate;

		// Read before the submission lands, because what the celebration marks is
		// the *moment* the day becomes complete — the last goal with nothing on
		// it getting something. A day that was already covered stays covered
		// however many more entries are added to it, and celebrating each of
		// those made the screen a thing to dismiss rather than an event.
		const wasCompleteBefore = coversEveryGoal(ratingLogs, savedForDate);

		setIsSaved(true);
		setShowSaveConfirmation(true);
		setSaveTick((tick) => tick + 1);

		// The diary entry has to wait for the rating: it carries that rating
		// log's id, which is what puts the two on one row of the Journey day
		// timeline and the report. Sent in parallel, the entry is saved
		// unlinked and never appears next to the face it was written with.
		saveRatingLog(
			{
				goalSettingId,
				rating: selectedRating,
				date: entryDate,
				startTime: entryTime,
			},
			currentUserId,
		)
			.then((logs) => {
				const ratingLogId = logs?.[0]?.id;
				if (!comment.trim()) return null;
				return (
					saveDiaryEntry(
						{
							goalSettingId,
							content: comment.trim(),
							ratingLogId,
							date: entryDate,
						},
						currentUserId,
					)
						// Caught here rather than at the end of the chain: by this
						// point the rating is on the server, and retracting the
						// confirmation over the note would invite a second save and
						// a duplicate rating. The note is what was lost, not the entry.
						.catch((err) => {
							console.error('Failed to save diary entry:', err);
							return null;
						})
				);
			})
			// Both reads describe the state *after* this submission, so they run
			// once it has landed rather than alongside it.
			.then(() => Promise.all([loadCareTip(), loadRatingLogs()]))
			.then(([tip, logs]) => {
				// Only a submission for today can have produced a tip. The tip
				// endpoint answers for today whatever day was filled in, so
				// revealing it after a backdated save would present a tip the
				// person may have already read as an answer to what they just
				// wrote about last Tuesday.
				const hasTipToShow = Boolean(tip) && savedForDate === todayISO();

				// The last goal of the day. The tip is revealed first and the
				// celebration waits behind it, so the delay is spent reading the
				// tip rather than looking at a page about to be taken away.
				if (hasTipToShow) revealCareTip();

				if (!wasCompleteBefore && coversEveryGoal(logs, savedForDate)) {
					clearTimeout(completionTimer.current);
					completionTimer.current = track(
						setTimeout(() => setShowCompletion(true), COMPLETION_DELAY_MS),
					);
				}
			})
			.catch((err) => {
				console.error('Failed to save tracking data:', err);
				// Both flags were set before the request went out. Nothing hides
				// them on a timer any more, so a failure has to retract them
				// here or the page claims an entry that was never filed.
				setIsSaved(false);
				setShowSaveConfirmation(false);
			});
	};

	// Handle next goal
	const handleNextGoal = () => {
		if (allGoals.length > 1) {
			const nextIndex = (currentGoalIndex + 1) % allGoals.length;
			onNextGoal(nextIndex);
		}
	};

	return (
		<div className={styles.container}>
			<Banner onLogoClick={onLogoClick} />
			<div className={styles.content} ref={contentRef}>
				{showCompletion ? (
					// The celebration takes the whole page once the day is covered.
					// It waits COMPLETION_DELAY_MS behind the care tip, so the tip
					// the last submission produced gets read first.
					<div className={styles.completionContainer}>
						<div className={styles.completionIcon}>
							<PartyPopper size={48} color='#10B981' />
						</div>
						<h1 className={styles.completionTitle}>Great job!</h1>
						<p className={styles.completionDescription}>
							You've successfully tracked all your goals. You can now view your
							self-care journey.
						</p>

						<div className={styles.completionActions}>
							<button
								className={styles.journeyButton}
								onClick={onNavigateToJourney}
							>
								View My Journey
								<ArrowRight size={20} />
							</button>

							{/* The day being covered does not mean there is nothing left to
							    do here — another entry, or the tip that just arrived. It
							    cannot come back on its own: the day is complete now, so no
							    later submission is the moment it became complete. */}
							<button
								type='button'
								className={styles.backToTrackingButton}
								onClick={() => setShowCompletion(false)}
							>
								<ArrowLeft size={18} />
								Back to Tracking
							</button>
						</div>
					</div>
				) : (
					<div className={styles.hero}>
						<PageTitle
							icon={trackingFeature.icon}
							title={trackingFeature.title}
							color={trackingFeature.color}
						/>

						{/* Account-level nudge (shown when there are no tips today) */}
						{prompt?.message && (
							<div className={styles.promptCallout}>
								<Bell size={20} className={styles.promptIcon} />
								<p className={styles.promptMessage}>{prompt.message}</p>
							</div>
						)}

						{/* Single Combined Card: Question + Rating + Notes */}
						<div className={styles.trackingCard}>
							{/* Question Section */}
							<h2 className={styles.questionTitle}>
								How is your{' '}
								<span className={styles.goalHighlight}>
									{currentGoal?.name || currentGoal?.title}
								</span>{' '}
								today?
							</h2>

							{/* Rating Scale */}
							<div className={styles.ratingScale}>
								{ratingScale.map((rating) => {
									const IconComponent = rating.icon;
									const isSelected = selectedRating === rating.value;

									return (
										<button
											key={rating.value}
											className={`${styles.ratingButton} ${
												isSelected ? styles.ratingButtonSelected : ''
											}`}
											onClick={() => handleRatingSelect(rating.value)}
											style={{
												borderColor: isSelected ? rating.color : rating.color,
												backgroundColor: isSelected
													? `${rating.color}30`
													: rating.bgColor,
											}}
										>
											<IconComponent color={rating.color} />
										</button>
									);
								})}
							</div>

							{/* Nothing else on the page shows that a goal has already been
						    rated, so a day half filled in looks exactly like an empty one.
						    It sits under the faces rather than down by the date controls:
						    the question it answers — "have I already done this one?" — is
						    asked at the moment of reaching for a face. */}
							{submittedTimes.length > 0 && (
								<p className={styles.submittedBanner}>
									<CheckCircle size={16} className={styles.submittedIcon} />
									<span>
										{entryDate === todayISO()
											? `Already submitted today at ${joinTimes(submittedTimes)}`
											: `Already submitted on ${formatMediumDate(
													entryDate,
												)} at ${joinTimes(submittedTimes)}`}
									</span>
								</p>
							)}

							{/* Comment Section */}
							<div className={styles.commentSection}>
								<h3 className={styles.sectionTitle}>DIARY ENTRY</h3>
								<h3 className={styles.sectionTitle}>
									How are you feeling? Any challenges or successes to note about{' '}
									<span className={styles.goalHighlight}>
										{currentGoal?.name || currentGoal?.title}
									</span>
									?
								</h3>
								<textarea
									className={styles.commentTextarea}
									value={comment}
									onChange={(e) => {
										setComment(e.target.value);
										markUnsaved();
									}}
									rows={4}
								/>

								{/* Save Button */}
								<div className={styles.saveSection}>
									<div className={styles.saveRow}>
										{/* When this entry is for, sat next to the button that
									    files it. Backdating is what lets a week of history
									    exist at all — without it the Journey views can only
									    ever show the day the app was opened. */}
										<div className={styles.whenGroup}>
											<div
												className={`${styles.whenRow} ${
													entryDate !== todayISO() ? styles.whenRowPast : ''
												}`}
											>
												<label className={styles.whenField}>
													<span className={styles.whenLabel}>Date</span>
													<input
														type='date'
														className={styles.whenInput}
														value={entryDate}
														max={todayISO()}
														onChange={(e) => {
															setEntryDate(e.target.value || todayISO());
															setWhenEdited(true);
															markUnsaved();
														}}
													/>
												</label>
												<label className={styles.whenField}>
													<span className={styles.whenLabel}>Time</span>
													<input
														type='time'
														className={styles.whenInput}
														value={entryTime}
														onChange={(e) => {
															setEntryTime(e.target.value);
															setWhenEdited(true);
															markUnsaved();
														}}
													/>
												</label>
												{whenEdited && (
													<button
														type='button'
														className={styles.nowButton}
														onClick={resetWhenToNow}
													>
														Now
													</button>
												)}
											</div>

											{/* Care tips answer what was logged today, so a day
										    filled in after the fact does not get one. Said
										    here rather than left as a silent absence: the
										    tip card below would otherwise look broken. */}
											{entryDate !== todayISO() && (
												<p className={styles.pastNote}>
													<Lightbulb
														size={13}
														className={styles.pastNoteIcon}
													/>
													<span>
														Entries for an earlier day don't come with a care
														tip.
													</span>
												</p>
											)}
										</div>

										<button
											className={`${styles.saveButton} ${
												!selectedRating ? styles.saveButtonDisabled : ''
											}`}
											onClick={handleSave}
											disabled={!selectedRating}
										>
											{isSaved ? (
												<>
													<CheckCircle size={20} />
													Saved
												</>
											) : (
												<>
													<Save size={20} />
													Save
												</>
											)}
										</button>
									</div>

									{/* Save Confirmation */}
									{showSaveConfirmation && (
										// Keyed on the submission count so a second save replays
										// the animation instead of sitting there unchanged, which
										// reads as the answer to the previous one.
										<div key={saveTick} className={styles.saveConfirmation}>
											<CheckCircle size={16} />
											Submitted successfully!
										</div>
									)}
								</div>
							</div>
						</div>

						{/* Care Tip Card */}
						<div
							ref={careTipRef}
							className={`${styles.careTipCard} ${
								tipRevealed ? styles.careTipRevealed : ''
							}`}
						>
							{/* The card always answers for today, whatever day the entry
						    above it is being filled in for. Saying so stops it from
						    looking stale, or like it failed to follow the date. */}
							<h3 className={styles.careTipTitle}>
								Care Tip
								{careTip && <span className={styles.careTipToday}>Today</span>}
							</h3>
							<p className={styles.careTipContent}>
								{careTip?.message ?? NO_NEW_TIPS_MESSAGE}
							</p>

							{/* The links out and the keep-this button share the last line:
						    stacked, they left a band of empty card between them. The links
						    get the same treatment as on the Resources page, which is where
						    a kept tip is read again. */}
							{careTip && (
								<div className={styles.careTipFooter}>
									{careTip.resources?.length > 0 && (
										<ul className={styles.resourceList}>
											{careTip.resources.map((resource) => (
												<li key={resource.id}>
													<a
														className={styles.resourceLink}
														href={resource.url}
														target='_blank'
														rel='noopener noreferrer'
													>
														<ExternalLink className={styles.resourceLinkIcon} />
														<span>{resource.name}</span>
													</a>
												</li>
											))}
										</ul>
									)}

									<div className={styles.careTipActions}>
										{savedTipLogId === careTip.logId && (
											<span className={styles.saveConfirmation}>
												<CheckCircle size={16} />
												Saved successfully!
											</span>
										)}
										<button
											type='button'
											className={`${styles.saveTipButton} ${
												careTip.isFavorite ? styles.saveTipButtonSaved : ''
											}`}
											onClick={handleSaveTip}
											disabled={careTip.isFavorite}
										>
											{careTip.isFavorite ? (
												<>
													{/* Filled where the unsaved state is outlined, the same
													    pair the Resources page uses on "Remove Tip". */}
													<Bookmark size={18} fill='currentColor' />
													Saved
												</>
											) : (
												<>
													<Bookmark size={18} />
													Save Tip
												</>
											)}
										</button>
									</div>
								</div>
							)}
						</div>

						{allGoals.length > 1 && (
							<div className={styles.nextGoalSection}>
								<button
									className={styles.nextGoalButton}
									onClick={handleNextGoal}
								>
									Next
									<ArrowRight size={20} />
								</button>
								<p className={styles.goalProgress}>
									Goal {currentGoalIndex + 1} of {allGoals.length}
								</p>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default TrackingPage;
